import { useState, useEffect, useCallback } from 'react';
import {
  getClients,
  getClientById,
  createClient,
  updateClient,
  deleteClient,
  searchClients,
  getClientsStats
} from '../../../../ApiCalls/mantisFlowsClients';

export const useClients = (clientId) => {
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  
  // Filter state
  const [filters, setFilters] = useState({
    name: '',
    email: '',
    status: 'all',
    is_company: 'all',
    country: ''
  });

  // Fetch clients with filters
  const fetchClients = useCallback(async (appliedFilters = {}) => {
    if (!clientId) return;
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Filter out "all" values before sending to API
      const cleanFilters = Object.fromEntries(
        Object.entries(appliedFilters).filter(([_, value]) => 
          value !== '' && value !== 'all' && value !== null && value !== undefined
        )
      );
      
      const response = await getClients(clientId, cleanFilters);
      
      if (response.success) {
        setClients(response.data || []);
        setTotalItems(response.data?.length || 0);
      } else {
        setError(response.message || 'Error al cargar clientes');
        setClients([]);
      }
    } catch (err) {
      setError(err.message || 'Error de conexión');
      setClients([]);
      console.error('Error fetching clients:', err);
    } finally {
      setIsLoading(false);
    }
  }, [clientId]);

  // Fetch client statistics
  const fetchStats = useCallback(async () => {
    if (!clientId) return;
    
    try {
      const response = await getClientsStats(clientId);
      if (response.success) {
        setStats(response.data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, [clientId]);

  // Create new client
  const handleCreateClient = useCallback(async (clientData) => {
    if (!clientId) throw new Error('Client ID is required');
    
    try {
      setIsLoading(true);
      const response = await createClient(clientId, clientData);
      
      if (response.success) {
        await fetchClients(filters);
        await fetchStats();
        return { success: true, data: response.data };
      } else {
        throw new Error(response.message || 'Error al crear cliente');
      }
    } catch (err) {
      const error = err.message || 'Error al crear cliente';
      setError(error);
      throw new Error(error);
    } finally {
      setIsLoading(false);
    }
  }, [clientId, filters, fetchClients, fetchStats]);

  // Update existing client
  const handleUpdateClient = useCallback(async (customClientId, clientData) => {
    if (!clientId || !customClientId) throw new Error('Client IDs are required');
    
    try {
      setIsLoading(true);
      const response = await updateClient(clientId, customClientId, clientData);
      
      if (response.success) {
        await fetchClients(filters);
        await fetchStats();
        return { success: true, data: response.data };
      } else {
        throw new Error(response.message || 'Error al actualizar cliente');
      }
    } catch (err) {
      const error = err.message || 'Error al actualizar cliente';
      setError(error);
      throw new Error(error);
    } finally {
      setIsLoading(false);
    }
  }, [clientId, filters, fetchClients, fetchStats]);

  // Delete client
  const handleDeleteClient = useCallback(async (customClientId, additionalData = {}) => {
    if (!clientId || !customClientId) throw new Error('Client IDs are required');
    
    try {
      setIsLoading(true);
      const response = await deleteClient(clientId, customClientId, additionalData);
      
      if (response.success) {
        await fetchClients(filters);
        await fetchStats();
        return { success: true };
      } else {
        throw new Error(response.message || 'Error al eliminar cliente');
      }
    } catch (err) {
      const error = err.message || 'Error al eliminar cliente';
      setError(error);
      throw new Error(error);
    } finally {
      setIsLoading(false);
    }
  }, [clientId, filters, fetchClients, fetchStats]);

  // Search clients
  const handleSearchClients = useCallback(async (searchTerm) => {
    if (!clientId || !searchTerm) return;
    
    try {
      setIsLoading(true);
      const response = await searchClients(clientId, searchTerm);
      
      if (response.success) {
        setClients(response.data || []);
        setTotalItems(response.count || 0);
      } else {
        setError(response.message || 'Error en la búsqueda');
      }
    } catch (err) {
      setError(err.message || 'Error en la búsqueda');
      console.error('Error searching clients:', err);
    } finally {
      setIsLoading(false);
    }
  }, [clientId]);

  // Apply filters
  const applyFilters = useCallback((newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page
    fetchClients(newFilters);
  }, [fetchClients]);

  // Clear filters
  const clearFilters = useCallback(() => {
    const emptyFilters = {
      name: '',
      email: '',
      status: 'all',
      is_company: 'all',
      country: ''
    };
    setFilters(emptyFilters);
    setCurrentPage(1);
    fetchClients(emptyFilters);
  }, [fetchClients]);

  // Pagination helpers
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentClients = clients.slice(startIndex, endIndex);

  const goToPage = useCallback((page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  }, [totalPages]);

  const changeItemsPerPage = useCallback((newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  }, []);

  // Initialize
  useEffect(() => {
    if (clientId) {
      fetchClients();
      fetchStats();
    }
  }, [clientId, fetchClients, fetchStats]);

  return {
    // Data
    clients: currentClients,
    allClients: clients,
    stats,
    
    // State
    isLoading,
    error,
    
    // Pagination
    currentPage,
    totalPages,
    itemsPerPage,
    totalItems,
    startIndex: startIndex + 1,
    endIndex: Math.min(endIndex, totalItems),
    
    // Filters
    filters,
    
    // Actions
    fetchClients,
    createClient: handleCreateClient,
    updateClient: handleUpdateClient,
    deleteClient: handleDeleteClient,
    searchClients: handleSearchClients,
    applyFilters,
    clearFilters,
    goToPage,
    changeItemsPerPage,
    refresh: () => fetchClients(filters),
    
    // Helpers
    hasNextPage: currentPage < totalPages,
    hasPrevPage: currentPage > 1
  };
}; 